import { DataNode, EventDataNode } from 'antd/lib/tree';
import { NodeDragEventParams } from 'rc-tree/lib/contextTypes';
import React, { Key } from 'react';
import { AlarmModel } from './AlarmModel';
import { ItemsPagination, OperatorsDetail, PaginationSetting, RedlineConfigValues, SelectOptions, TupleTyped } from './CommonModel';
import { DeviceObjectType, ObjectTypes, PortStyle } from './DeviceAdminModel';
import { UserDefinedFieldSetup } from './UserDefinedFields';

export type SpaceObject = {
	path: string;
	tags: string;
	node: string;
	area: string;
	outerhtml: string;
	occupancy: string;
	name: string;
	type: string;
	id: string;
	defaultcolor: unknown;
	highlightcolor: unknown;
	selectcolor: unknown;
	layer: string;
	group: string;
	linked: string;
	attributes: string;
	attributeStatus: string;
	displaySpaceObject: string;
	translate_x: number;
	translate_y: number;
	zvalue: number;
	scale_x: number;
	scale_y: number;
	rotate: number;
	transform: number;
	svgx: string;
	svgy: string;
	dwgx: string;
	dwgy: string;
	customContent: GraphicDeviceInfo;
	pageNumber: number;
	pageName: string;
	block: string;
	blockAttributeId: string;
	blockAttributeCount: string;
	clickhandler: string;
	parent: string;
};

export type RedLine = {
	node: string;
	outerhtml: string;
	name: string;
	type: string;
	id: string;
	layer: string;
	group: string;
	color: string;
	strokeWidth: string;
	fill: string;
	username: string;
	userid: string;
	currentPage: string;
	triange_design: string;
	polypath_arrow: string;
	redline_text: string;
	redline_font_size: number;
	fill_opacity: string;
	transform: string;
	drawingRotation: string;
};

export type RedlineValues = {
	RedlineObjects: RedLine[];
};

export type Coordinate = {
	x: number;
	y: number;
};

export type ObjectBaseProperties = {
	ObjType: C_ObjectType | C_PictureTypeCustom;
	Id: number;
	Name: string;
};

export type ObjectFontProperties = {
	Id: number;
	DisplayText: string;
	TextColorHex: string;
	FontSize: number;
	FontFamily: string;
	FontStyle: number;
	FontContainerStyle: number;
	AutoSize: boolean;
};

export type ObjectAppearanceProperties = {
	Id?: number;
	Visible?: boolean;
	Transparency: number;
	BackColor?: string;
	BackStyle?: number;
	FillColor?: string;
	FillStyle?: number;
	LineColor?: string;
	LineStyle?: number;
	LineWidth?: number;
};

export type ObjectPositionProperties = {
	Id: number;
	Top: number;
	Bottom: number;
	Left: number;
	Right: number;
	Rotation: number;
};

export type GenericGraphicObject = {
	PositionInfo: ObjectPositionProperties;
} & ObjectBaseProperties;

export type GraphicText = {
	DataField: string;
	UDF: number;
	FontInfo: ObjectFontProperties;
	PositionInfo: ObjectPositionProperties;
	AppearanceInfo: ObjectAppearanceProperties;
} & ObjectBaseProperties;

export type VelocityObject = {
	Address: string;
	CanvasId: number;
	DisplayText: string;
	CredentialShow: boolean;
	CredentialDelay: number;
	DefaultVO: boolean;
	HasAckAlarms: boolean;
	HasAlarms: boolean;
	InitialState: SpecialEventType[];
	IsValidatedInDB: boolean;
	Data?: string | number;
	Layer?: number;
	ObjectId: number;
	ObjectType: ObjectTypes;
	UniqueId: string;
	PositionInfo: ObjectPositionProperties;
} & ObjectBaseProperties;

export type ShapeObject = {
	ShapeType: number;
	UDF: number;
	PositionInfo: ObjectPositionProperties;
	AppearanceInfo: ObjectAppearanceProperties;
} & ObjectBaseProperties;

export type PointMonitoringZone = {
	MaskedColor: string;
	SecureColor: string;
	AlarmColor: string;
	UnknownColor: string;
	Points: MonitoringPoint[];
	Coordinates: Coordinate[];
	FontInfo: ObjectFontProperties;
	PositionInfo: ObjectPositionProperties;
} & ObjectBaseProperties;

export type GraphicCommandSet = {
	LinkTemplateId: number;
	CommandSetData: number;
	FontInfo: ObjectFontProperties;
	PositionInfo: ObjectPositionProperties;
} & ObjectBaseProperties;

export type GraphicLink = {
	LinkId: number;
	CanvasId: number;
	FontInfo: ObjectFontProperties;
	PositionInfo: ObjectPositionProperties;
} & ObjectBaseProperties;

export type BadgeStudioMagstripe = {
	MagstripesData: string;
	MagstripeDisplayText: string;
	MagstripeFont: string;
	MagstripeFontColor: string;
	PositionInfo: ObjectPositionProperties;
	AppearanceInfo: ObjectAppearanceProperties;
} & ObjectBaseProperties;

export type BadgeStudioImage = {
	ImageType: ImageType;
	FileExtension: string;
	ImageDataBase64: string;
	PositionInfo: ObjectPositionProperties;
	AppearanceInfo: ObjectAppearanceProperties;
} & ObjectBaseProperties;

export type BadgeStudioBarCode = {
	Symbology: number;
	Data: string;
	Comment: string;
	Supplemental: string;
	NarrowBarWidth: number;
	ReductionGain: number;
	Height: number;
	NarrowWideRatio: number;
	CommentOnTop: boolean;
	IncludeText: boolean;
	TextOnTop: boolean;
	ShowQuietZones: boolean;
	ShowBearerBars: boolean;
	ShowCheckCharacter: boolean;
	AppendLuhnCheckDigit: boolean;
	BarcodeFont: string;
	BarcodeFontColor: string;
	BarcodeBackgroundStyle: boolean;
	BarcodeVisible: boolean;
	PositionInfo: ObjectPositionProperties;
} & ObjectBaseProperties;

export type BadgeStudioSpaceObjects = {
	Id: number;
	Texts: GraphicText[];
	BadgeMagstripes: BadgeStudioMagstripe[];
	ShapeObjects: ShapeObject[];
	Images: BadgeStudioImage[];
	BarCodes: BadgeStudioBarCode[];
	DBName: string;
	FileName: string;
};

export type SpaceObjectSerialize = {
	Id: number;
	DBName: string;
	Status: GraphicStatus;
	FileName: string;
	VelocityObjects: VelocityObject[];
	ShapeObjects: ShapeObject[];
	PointMonitoringZones: PointMonitoringZone[];
	CommandSets: GraphicCommandSet[];
	Links: GraphicLink[];
	Texts: GraphicText[];
};

export type ReplacePathInfo = {
	id: string;
	path: string;
	width: number;
	height: number;
	x: number;
	y: number;
};

export type GraphicDeviceInfo = {
	DeviceId: number;
	Address: string;
	InAlarm: boolean;
	HasAlarm: boolean;
	NormallyOpen: boolean;
	LineModuleType: number;
	ShowCredential: boolean;
	DeviceAlarms: DeviceAlarm[];
	PointMonitoringZonePoints: MonitoringPoint[];
	PictureType: C_PictureTypeCustom;
	DeviceType: DeviceType;
	DeviceObjectType: DeviceObjectType;
	//TODO: FIX THIS
	VOType: any;
	ObjectKey: string;
	VOKey?: string;
	CObjectType?: C_ObjectType;
	AlarmColor: string;
	SecureColor: string;
	MaskedColor: string;
	UnknownColor: string;
};

export type DeviceAlarm = {
	AlarmId: number;
	ImageId: number;
};

export enum GraphicChangeType {
	None = 0,
	DirectoryChange = 1,
	DeviceRename = 2,
	AlarmPermission = 3,
	ReloadTopology = 4,
	ControllerAddress = 5,
	DeleteVelocityObject = 6,
	LiveEvent = 7,
	LiveAlarm = 8,
	IntrinsicRelay = 9,
	IntrinsicInput = 10,
	IntrinsicPMZ = 11,
}

export type GraphicMessage = {
	ChangeType: GraphicChangeType;
	SyncMessage: GraphicSyncMessage;
	LiveMessage: GraphicLiveMessage;
};

export type GraphicSyncMessage = {
	ParentCanvasId: number;
	ChildrenCanvasId: number;
	DeviceKey: string;
	DeviceName: string;
	Address: string;
	OldAddress: string;
	DeleteChildren: boolean;
	DeleteVelocityObjects: DeleteVelocityObject[];
	AlarmPermissions: GraphicAlarmPermission;
};

export type GraphicLiveMessage = {
	Address: string;
	DoorAddress: string;
	EventId: number;
	SpecialValue: SpecialEventType;
	Description: string;
	AccessText: string;
	IntrinsicEvents: IntrinsicEvent[];
};

export type IntrinsicEvent = {
	Address: string;
	DoorAddress: string;
	EventId: number;
	SpecialValue: SpecialEventType;
};

export type DeleteVelocityObject = {
	MetaDrawCurrent: number;
	SpaceObjectId: number;
	Address: string;
};

export type ChangePMZStateIndex = {
	pmzIndex: number;
	inputIndex: number;
};

export enum C_PictureTypeCustom {
	MDOT_NONE = -1,
	MDOT_CONTAINER = 0,
	MDOT_LINE = 1,
	MDOT_RECTANGLE = 2,
	MDOT_ROUNDRECT = 3,
	MDOT_ELLIPSE = 4,
	MDOT_ARC = 5,
	MDOT_PIE = 6,
	MDOT_CHORD = 7,
	MDOT_POLYLINE = 8,
	MDOT_POLYGON = 9,
	MDOT_TEXT = 10,
	MDOT_BITMAP = 11,
	MDOT_IMAGE = 12,
	MDOT_BEZIER = 14,
	MDOT_POLYPOLYGON = 15,
	MDOT_SOLIDPICTURE = 16,
	MDOT_FLOODFILL = 19,
	MDOT_LINK = 21,
	MDOT_DIMLINE = 22,
	MDOT_POLYLINK = 23,
	MDOT_DBBARCODE = 50,
	MDOT_DBMAGSTRIPE = 51,
	MDOT_DBIMAGE = 52,
	MDOT_DBTEXT = 53,
	MDOT_PHYSICALZONE = 54,
	MDOT_CONTROLZONE = 55,
	MDOT_VIO = 56,
	MDOT_VSO = 57,
	MDOT_ALARMINFO = 58,
	MDOT_COMMANDSET = 59,
	MDOT_CUSTOMLINK = 60,
	MDOT_CREDINFO = 61,
	MDOT_PMZ = 62,
	MDOT_IDS_AREA = 63,
}

export enum C_ObjectType {
	OT_CONTAINER = 0,
	OT_LINE = 1,
	OT_RECTANGLE = 2,
	OT_ROUNDRECT = 3,
	OT_ELLIPSE = 4,
	OT_ARC = 5,
	OT_PIE = 6,
	OT_CHORD = 7,
	OT_POLYLINE = 8,
	OT_POLYGON = 9,
	OT_TEXT = 10,
	OT_BITMAP = 11,
	OT_IMAGE = 12,
	OT_BEZIER = 14,
	OT_POLYPOLYGON = 0xf,
	OT_SOLIDPICTURE = 0x10,
	OT_FLOODFILL = 19,
	OT_LINK = 21,
	OT_DIMLINE = 22,
	OT_POLYLINK = 23,
	OT_TRIANGLE = 30,
	OT_DIAMOND = 0x1f,
	OT_PENTAGON = 0x20,
	OT_STAR = 33,
	OT_HEXAGON = 34,
	OT_OCTAGON = 35,
}

export type MD_ObjectType = C_PictureTypeCustom | C_ObjectType;

export enum E_ObjectTypes {
	OTYP_NONE = 0,
	OTYP_MDOBJECT,
	OTYP_CANVAS,
	OTYP_OBJECTLIBVXO,
}

export enum BCODER3 {
	Code39 = 1,
	FullASCII = 2,
	UPCa = 3,
	UPCe = 4,
	EAN8 = 5,
	EAN13 = 6,
	BookLan = 7,
	I2of5 = 8,
	D2of5 = 9,
	CodaBar = 10,
	Code93 = 11,
	Code128 = 12,
	EANUCC128 = 13,
	PDF = 14,
	Zip = 15,
}

export type MonitoringPoint = {
	Id: number;
	ObjectId: number;
	InputType: InputType;
	Address: string;
	IsEnabled: boolean;
	InAlarm: boolean;
	IsNormallyOpen: boolean;
	InputLMType: number;
	ContactStatus: number;
	MaskSource: number;
} & MonitoringPointState;

export type DeviceInfoGraphicEvent = {
	Address: string;
	NormallyOpen: boolean;
	LineModuleType: number;
	ShowCredential: boolean;
	MonitoringPoints: MonitoringPoint[];
};

export type MonitoringPointState = {
	Contact1Secured: PointState;
	Contact3Secured: PointState;
	MaskState: PointState;
};

export enum SpecialEventType { //Cause Value
	None = 0,
	ContactOneClosed = 1, //Door Closed
	ContactOneOpen = 2, //Door Open
	ContactTwoClosed = 3, //RQE Active
	ContactTwoOpen = 4, //RQE Normal
	ContactThreeClosed = 5, //Tamper Secure
	ContactThreeOpen = 6, //Tamper Active
	InputMasked = 7, //Input Masked
	InputUnmasked = 8, //Input Unmasked
	RelayActive = 9, //Door Unlocked
	RelayInactive = 10, //Door Locked
	Unknown = 11,
	AlarmActive = 12,
	AlarmAcknowledged = 13,
	ClearedAlarm = 14,
}

export enum PointState {
	PMZ_POINT_STATE_ALARM = -1,
	PMZ_POINT_STATE_FALSE = 0,
	PMZ_POINT_STATE_TRUE = 1,
	PMZ_POINT_STATE_UNKNOWN = 2,
	PMZ_POINT_STATE_NA = 3,
}

export enum ZoneState {
	PMZ_ZONE_STATE_UNKNOWN = 0,
	PMZ_ZONE_STATE_MASKED = 1,
	PMZ_ZONE_STATE_SECURE = 2,
	PMZ_ZONE_STATE_ALARM = 3,
}

export enum InputType {
	None = 0,
	Standard = 1,
	Expansion = 2,
	DoorContact = 3,
}

export enum TriggerType { //Cause Type
	None = 0,
	EventId = 1, //Specific Event
	EventType = 2, //Event Type
	Click = 3, //Click
	Intrinsic = 4, //Basic Device States
	Acknowledge = 5,
}

export type SpaceObjectStyle = {
	fill: string;
	'Fill-Opacity': string;
	stroke: string;
	'stroke-width': number;
	'stroke-opacity': string;
	'stroke-linejoin': string;
};

export type SpaceObjectAttribute = {
	name: string;
	value: string;
};

export type GraphicToGroups = {
	CanvasID: number;
	GroupID: number;
	ParentID: number;
	TreeLevel: number;
	SystemDefault: boolean;
	CanvasesToGroupsId: number;
	DefaultCanvas: boolean;
	LoadOnStart: boolean;
	Graphic: Graphic;
	Children?: GraphicToGroups[];
};

export type Graphic = {
	CanvasID: number;
	Type: number;
	Filename: string;
	DisplayName: string;
	InDesignMode: boolean;
	LastUpdated: Date;
	Scale: number;
	VOTypeID: number;
	DirectoryStructure: string;
	SystemDefault: boolean;
};

export type GraphicAlarmPermission = {
	CanAcknowledge: boolean;
	CanAcknowledgeMultiple: boolean;
	CanClear: boolean;
	CanClearMultiple: boolean;
	CanClearRestricted: boolean;
	MaxAlarmLevel: number;
	RequireNoteMultipleAcknowledge: boolean;
	RequireNoteMultipleClear: boolean;
	IsClearUnacknowledgedAllowed: boolean;
	RequireNoteOnAcknowledge: boolean;
	RequireNoteOnClear: boolean;
};

export enum ActionToExecute {
	None = 0,
	ConfigureDevice = 1,
	CustomLink = 2,
	Design = 4,
	Delete = 8,
	Duplicate = 16,
	Edit = 32,
	ExportToFile = 64,
	ImportFromFile = 128,
	New = 256,
	Rename = 512,
	Refresh = 1024,
	Save = 2048,
}

export type GraphicCurrentAction = {
	key: React.Key;
	type: ActionToExecute;
};

export type OnClickDirectoryContextMenu = {
	id: React.Key;
	actionType: ActionToExecute;
};

export type TreeDataNodeCustom<T> = {
	options?: T;
	children: TreeDataNodeCustom<T>[];
	parentId?: number;
	treeLevel?: number;
} & DataNode;

export type TreeEventDataNodeCustom<T> = {
	options?: T;
	children: TreeEventDataNodeCustom<T>[];
	displayName: string;
	parentId: number;
	treeLevel: number;
} & EventDataNode<ObjectLibraryTreeNode>;

export type GraphicTreeNode = TreeDataNodeCustom<SelectOptions<ActionToExecute>[]>;

export type GraphicTreeEventDataNode = TreeEventDataNodeCustom<SelectOptions<ActionToExecute>[]>;

export type UpdateTreeLevelNode = {
	canvasId: number;
	displayName: string;
	parentId: number;
	treeLevel: number;
	children: UpdateTreeLevelNode[];
};

export type GetGraphicDirectoryByRequest = {
	PaginationSetting: PaginationSetting;
	GraphicType: GraphicType;
	OperatorsIdFilters: number[];
	Status: GraphicStatus;
};

export type GraphicDirectoryInfo = {
	Id: number;
	Name: string;
	LastUpdated: Date | string;
	Status?: GraphicStatus;
	Type: GraphicType;
	CreatedByUserId: number;
	LastUpdatedByUserId: number;
};

export type GraphicDirectoryInfoPagination = {
	MapDirectoriesInfo: GraphicDirectoryInfo[];
} & ItemsPagination;

export type BadgeDirectoryInfo = {
	Id: number;
	Name: string;
	LastUpdated: Date | string;
	Type: GraphicType;
};

export type BadgeDirectoryInfoPagination = {
	BadgeDirectoryInfo: BadgeDirectoryInfo[];
} & ItemsPagination;

export type GraphicSorterConfigTyped<T> = {
	direction: GraphicSortDirections;
	field: T;
};

export enum GraphicSortDirections {
	None,
	Descend,
	Ascend,
}

export enum MapStudioSortOptions {
	None,
	Name,
	LastUpdated,
}

export type GraphicStudioFilterBy = {
	OperatorNames: OperatorsDetail[];
	Status?: GraphicStatus;
};

export enum GraphicDirectoryColumns {
	None = 0,
	Name = 1,
	LastUpdated = 2,
	Type = 3,
	Status = 4,
}

export enum GraphicStatus {
	NA = 0,
	Draft,
	Published,
}

export enum GraphicType {
	None = 0,
	VelocityCore,
	Web,
}

export enum GraphicPageMode {
	Design = 0,
	Live,
}

export enum GraphicActionType {
	None = 0,
	Directory = 1,
	ModalDirectory = 2,
	Canvas = 4,
}

export enum ToolbarSection {
	Drawing = 0,
	SpaceObject,
}

export enum ContextMenuObjTypes {
	VIO = 1,
	TABSTRIP,
	OBL_TOP,
	OBL_VIO,
	OBL_VSO,
	OBL_FOLDER,
	OBL_CLM,
	OBL_CONFIG,
	AQ_TREE_VO,
	AQ_TREE_ALARM,
	AQ_TREE_ROOT,
	DIR_ROOT_BADGE,
	DIR_ROOT,
	DIR_DRAWING,
	DIR_VO,
	DIR_BADGE_TEMPLATE,
	DTII_PMZ,
	IDS_AREA,
	VIO_DESIGN,
	TEXT_DESIGN,
	ALARM_INFO,
	CRED_INFO,
}

export type SpaceObjectType = {
	type: ContextMenuObjTypes;
	//TODO FIX THIS
	secondaryType: any;
};

export type GraphicTabInfo = {
	resourceStrings: GraphicResourceStrings;
};

export type GraphicResourceStrings = {
	acknowledgeAlarms: string;
	clearAlarms: string;
	accessFunctions: string;
	momentaryAccess: string;
	unlock: string;
	relock: string;
	toogleLock: string;
	controlFunctions: string;
	trigger: string;
	mask: string;
	unmask: string;
	momentaryMask: string;
	suppressOperate: string;
	suppressOperateRelease: string;
	forceOn: string;
	forceOnRelease: string;
	forceOff: string;
	forceOffRelease: string;
	lockDown: string;
	lockDownRelease: string;
	lockOpen: string;
	lockOpenRelease: string;
	viewCamera: string;
};

export enum ContextMenuIds {
	ID_IMAGEBASE = 10000,
	ID_MENU_ACK = 2300,
	ID_MENU_CLEAR = 2301,
	ID_MENU_ACCESS_FUNCTIONS = 2302,
	ID_MENU_MOMENTARY_ACCESS = 2303,
	ID_MENU_UNLOCK = 2304,
	ID_MENU_RELOCK = 2305,
	ID_MENU_CONTROL_FUNCTIONS = 2306,
	ID_MENU_TRIGGER = 2307,
	ID_MENU_MASK = 2308,
	ID_MENU_UNMASK = 2309,
	ID_MENU_MOMENTARY_MASK = 2310,
	ID_MENU_FORCE_ON = 2311,
	ID_MENU_FORCE_ON_RELEASE = 2312,
	ID_MENU_FORCE_OFF = 2313,
	ID_MENU_FORCE_OFF_RELEASE = 2314,
	ID_MENU_LOCK_DOWN = 2315,
	ID_MENU_LOCK_DOWN_RELEASE = 2316,
	ID_MENU_LOCK_OPEN = 2317,
	ID_MENU_LOCK_OPEN_RELEASE = 2318,
	ID_MENU_SHOW_CRED_INFO = 2319,
	ID_MENU_VIEW_CAMERA = 2320,
	ID_MENU_TOP_NEW_OBJECT = 2321,
	ID_MENU_TOP_NEW_FOLDER = 2322,
	ID_MENU_TABSTRIP_CLOSE = 2323,
	ID_MENU_TABSTRIP_DELETE = 2324,
	ID_MENU_TABSTRIP_DESIGN = 2325,
	ID_MENU_TOGGLELOCK = 2326,
	ID_MENU_SUPPRESSOPERATE = 2327,
	ID_MENU_SUPPRESSOPERATE_RELEASE = 2328,
	ID_EDIT_PASTE = 4001,
	ID_EDIT_COPY = 4003,
	ID_EDIT_CUT = 4004,
	ID_FILE_NEW = 4008,
	ID_FILE_OPEN = 4009,
	ID_FILE_SAVE = 4010,
	ID_FILE_PRINT = 4011,
	ID_FILE_G_PAGE_SETUP = 4012,
	ID_FILE_G_PRINT_PREVIEW = 4013,
	ID_APP_EXIT = 57665,
	ID_MISC = 350,
	ID_G_CNV_DRAWING = 2000,
	ID_G_CNV_BADGE = 2001,
	ID_G_CNV_VIO = 2002,
	ID_G_CNV_VSO = 2003,
	ID_G_IMPORT = 2004,
	ID_G_EXPORT = 2005,
	ID_MENU_OBL_TOP = 2340,
	ID_MENU_OBL_IMPORT_VIO = 2341,
	ID_MENU_OBL_IMPORT_VSO = 2342,
	ID_MENU_OBL_OPEN = 2343,
	ID_MENU_OBL_RENAME = 2344,
	ID_MENU_OBL_EXPORT_VIO = 2345,
	ID_MENU_OBL_EXPORT_VSO = 2346,
	ID_MENU_OBL_CLOSE = 2347,
	ID_MENU_OBL_DELETE = 2348,
	ID_MENU_OBL_DUPLICATE = 2349,
	ID_MENU_OBL_NEW_FOLDER_VIO = 2350,
	ID_MENU_OBL_NEW_FOLDER_VSO = 2351,
	ID_MENU_OBL_CUSTOM_LINK_MAN = 2352,
	ID_MENU_OBL_CONFIGURE = 2353,
	ID_MENU_AQ_ACK = 2354,
	ID_MENU_AQ_CLR = 2355,
	ID_MENU_AQ_LOCATE = 2356,
	ID_MENU_AQ_CONFIGURE = 2357,
	ID_MENU_AQ_NOTE = 2358,
	ID_MENU_AQ_REFRESH = 2359,
	ID_MENU_DIR_RENAME = 2360,
	ID_MENU_DIR_OPEN = 2361,
	ID_G_GROUP_HEADERANDFOOTER = 2015,
	ID_G_GROUP_POPUPICON = 2016,
	ID_G_GROUP_STYLES = 2017,
	ID_G_GALLERY_STYLES = 2018,
	ID_G_GALLERY_SHAPES = 2019,
	ID_G_GALLERY_COLORS = 2020,
	ID_G_GALLERY_LARGE_COLORS_POPUP = 2021,
	ID_G_GROUP_SHAPES = 2022,
	ID_G_TEXT_HIGHLIGHTCOLOR = 2023,
	ID_GALLERY_STYLES = 2024,
	ID_GALLERY_SHAPES = 2025,
	ID_GALLERY_COLORS = 2026,
	ID_GALLERY_LARGE_COLORS_POPUP = 2027,
	ID_G_FONT_STYLE = 2028,
	ID_G_TEXT_MORE_COLORS = 2029,
	ID_G_TEXT_ALIGN_TOP = 2030,
	ID_G_TEXT_ALIGN_MIDDLE = 2031,
	ID_G_TEXT_ALIGN_BOTTOM = 2032,
	ID_G_TEXT_ALIGN_LEFT = 2033,
	ID_G_TEXT_ALIGN_CENTER = 2034,
	ID_G_TEXT_ALIGN_RIGHT = 2035,
	ID_G_MODE_DESIGN = 2036,
	ID_G_MODE_BADGE = 2037,
	ID_G_MODE_GRAPHIC = 2038,
	ID_MENU_PMZ_MASK = 2401,
	ID_MENU_PMZ_UNMASK = 2402,
	ID_MENU_PMZ_MOMENTARY_MASK = 2403,
	ID_DESIGN_VIO_DELETE = 2410,
	ID_DESIGN_VIO_EDIT = 2411,
	ID_DESIGN_VIO_SET_PRIMARY = 2412,
	ID_DESIGN_VIO_CONFIGURE = 2413,
	ID_DESIGN_TEXT_EDIT = 2414,
	ID_LIVE_ALARM_INFO_ACK = 2420,
	ID_LIVE_ALARM_INFO_CLEAR = 2421,
	ID_LIVE_ALARM_INFO_ADD_NOTE = 2422,
	ID_LIVE_SHOW_CRED_INFO = 2423,
	ID_EDIT_DELETE = 2095,
	ID_EDIT_SELECT_ALL = 2096,
	ID_EDIT_UNDO = 145,
	ID_EDIT_REDO = 146,
	ID_MENU_DELETE_DRAWING = 2490,
	ID_MENU_END = 2400,
}

export type ContextMenu = {
	contextMenuItems: ContextMenuItem[];
};

export type ContextMenuItem = {
	disabled: boolean;
	groupBeginning: boolean;
	hasInput: boolean;
	id: ContextMenuIds;
	isInputChecked: boolean;
	label: string;
	subMenus: ContextMenuItem[];
};

export type ContextMenuRequest = {
	ContextMenuObjType: ContextMenuObjTypes;
	MenuId: number;
	ObjectAddress: string;
	ObjectKey: string;
	//TODO FIX THIS
	VOType: any;
	VOKey: string;
};

export type ContextMenuPosition = {
	x: number;
	y: number;
};

export type ExecuteFunctionRequest = {
	id: ContextMenuIds;
	spaceObject: SpaceObject;
};

export type DeleteSpaceObjectRequest = {
	drawingId: number;
	deviceId: number;
};

export enum DeviceType {
	Switcher = 'Switcher',
	CCTVCameraProperties = 'CCTV Camera Properties',
	CCTVCameraView = 'CCTV Camera View',
	Input = 'Input',
	Output = 'Output',
	Relay = 'Relay',
	Reader = 'Reader',
	Door = 'Door',
	Xbox = 'Xbox',
	Port = 'Port',
	CCTVPort = 'CCTVPort',
	Domain = 'Domain',
	Server = 'Server',
	Controller = 'Controller',
	ExpInput = 'ExpInput',
	ExpOutput = 'ExpOutput',
	DialUpLocation = 'Dialup Location',
}

export type FullDragEventParams = {
	dragNode: EventDataNode<ObjectLibraryTreeNode>;
	dragNodesKeys: Key[];
	dropPosition: number;
	dropToGap: boolean;
} & NodeDragEventParams;

export type DirectoryTreeOnSelectParams = {
	event: string;
	selected: boolean;
	node: EventDataNode<ObjectLibraryTreeNode>;
	selectedNodes: DataNode[];
	nativeEvent: MouseEvent;
};

export type DirectoryTreeOnExpandParams = {
	expanded: boolean;
	node: EventDataNode<ObjectLibraryTreeNode>;
	nativeEvent: MouseEvent;
};

export type ObjectSearchField = {
	ParentId: number;
	ObjectId: number;
	PortStyle: PortStyle;
	ObjectType: ObjectTypes;
};

export type ObjectLibraryTreeNode = {
	displayName?: string;
	options?: SelectOptions<ActionToExecute>[];
	children: ObjectLibraryTreeNode[];
	folderType?: ObjectLibraryFolder;
	isContextMenuValid?: boolean;
	isChecked?: boolean;
	allChildrenSelected?: boolean;
	isExpanded?: boolean; // TODO: Verify if it's in use
	parentNode?: ObjectLibraryTreeNode;
	extendedData?: ObjectLibraryTreeNodeExtendedData;
} & DataNode;

export type FloatingWindowsVisibility = {
	alarmQueue: boolean;
	devices: boolean;
	plottedDevices: boolean;
	objectProperties: boolean;
	quickAccess: boolean;
	birdEye: boolean;
	alreadyLoaded: boolean;
};

export type FloatingWindowsVisibilityProp = Partial<FloatingWindowsVisibility>;

export type ObjectLibraryTreeNodeExtendedData = {
	hasMockedData: boolean;
	actionToExecute?: ActionToExecute;
	objectId?: number;
	address?: string;
	objectType?: ObjectTypes;
	portStyle: PortStyle;
};

export enum ObjectLibraryTreeNodeIcon {
	NONE = 0,
	UNCHECKED,
	INDETERMINATE,
	CHECKED,
}

export enum ObjectLibraryFolder {
	NONE = 0,
	FOLDER,
	ITEM,
}

export type ObjectLibraryEventDataNode = {
	options?: SelectOptions<ActionToExecute>[];
	children: ObjectLibraryEventDataNode[];
	displayName: string;
	parentId: number;
	treeLevel: number;
	objectType?: ObjectTypes;
	parentKeys?: string[];
	folderType?: ObjectLibraryFolder;
	isContextMenuValid?: boolean;
	objectId?: number;
	address?: string; // TODO: Add this property to backend model
} & EventDataNode<ObjectLibraryTreeNode>;

export enum CanvasTypes {
	PT_VIO = 1,
	PT_Badge,
	PT_BackDrop,
	PT_VSO,
}

export type TypeCanvas = {
	type: CanvasTypes;
	systemDefault: boolean;
};

export type RenamePayload = {
	key: string | number;
	objectId: number;
	childId: number;
	newValue: string;
	parentKeys: string[];
	folderType: ObjectLibraryFolder;
	mainFolder?: ObjectLibraryFolder;
};

export type RenameVIO = {
	objectId: number;
	childId: number;
	displayName: string;
	folderType: ObjectLibraryFolder;
	mainFolder: ObjectLibraryFolder;
};

// * Context Menu Types
export enum ActionGraphicContextMenu {
	NONE = 0,
	ACKNOWLEDGED_ALARMS,
	CLEAR_ALARMS,
	DELETE,
	SET_AS_PRIMARY,
	CONFIGURE_DEVICE,
	SHOW_CREDENTIAL_INFO,
}

export type ActionGraphic = {
	action: ActionGraphicContextMenu;
};

export type TextObj = {
	bold: boolean;
	fontColor: string;
	fontFamily: string;
	fontSize: number;
	italic: boolean;
	lineThrough: boolean;
	text: string;
	underline: boolean;
};

export type DeviceAlarmStatus = {
	HasAlarms: boolean;
	HasAckAlarms: boolean;
	UniqueId: string;
};

export type AlarmQueueDevice = {
	Count: number;
	Name: string;
	Address: string;
	ObjectType: ObjectTypes;
	ObjectId: number;
} & DeviceAlarmStatus;

export type PendingItem = Pick<AlarmQueueTreeNode, 'Address' | 'ObjectType'>;

export type AlarmQueueTreeNode = Partial<AlarmModel> &
	Partial<AlarmQueueDevice> &
	Omit<DataNode, 'key' | 'children'> & { key: string; children?: AlarmQueueTreeNode[] };

export type ProccessLiveAlarmEventParams = {
	alarmAddress: string;
	isNormalAlarm?: boolean;
	velocityObject: Partial<VelocityObject>;
	isVelocityObjectUpdated?: boolean;
};

export type GraphicState = {
	graphicInfo: Partial<GraphicDirectoryInfo>;
	graphicActionType: GraphicActionType;
	dropDataNodes: ObjectLibraryTreeNode[];
	hasChanged: boolean;
	viewMode: GraphicPageMode;
	drawingMenuOptsDisabled: boolean;
	currentRedlineConfigValues: RedlineConfigValues;
	error: string;
	errorMessages: TupleTyped<number, string>[];
};

export type MapsState = {
	floatingWindowsVisibility: FloatingWindowsVisibility;
	alarmPermissions: GraphicAlarmPermission;
	showAlarmTableModal: boolean;
	showAlarmNoteModal: boolean;
	lastObjectDirectoryCoordinates: Coordinate;
	lastPlottedDevicesCoordinates: Coordinate;
	lastAlarmQueueCoordinates: Coordinate;
	spaceObject: Partial<SpaceObjectSerialize>;
} & GraphicState;

export type BadgeState = {
	badgeInfo: BadgeStudio;
	fontFamilies: string[];
	spaceObjects: Partial<BadgeStudioSpaceObjects>;
	udfs256: UserDefinedFieldSetup[];
	udfs: UserDefinedFieldSetup[];
} & GraphicState;

export type BaseFontPositionProperties = {} & ObjectBaseProperties & ObjectFontProperties & ObjectPositionProperties;

export type BadgeStudio = {
	Id: number;
	Name: string;
	PreviewImage: string;
	FileExtension: string;
	CreatedByUserId?: number;
	LastUpdatedByUserId?: number;
	CreatedDate?: Date;
	LastUpdated?: Date;
	Status: number;
	CanvasId?: number;
};

export type BadgeListInfo = {
	PageOrientation: number;
	DoubleSided: boolean;
	IsArchived: number;
} & BadgeStudio;

export type ImagePlaceHolder = {
	ImagePlaceHolderId: number;
	Caption: string;
};

export type IDSAreaBasicData = {
	AreaId: number;
	AreaName: string;
};

export type VOType = {
	VoTypeId: number;
	Name: string;
	MapId: number;
	MenuId: number;
};

export type PropertiesDTO = {
	SysDebugLogCoords: boolean;
	SysAlarmFlash: boolean;
	CObjectType: C_PictureTypeCustom | C_ObjectType;
	// MDOT_LINK
	SysLinkData: number;
	// MDOT_COMMANDSET
	SysCommandSetData: string;
	SysLinkTemplate: number;
	// MDOT_CUSTOMLINK
	SysCustomLinkData: number;
	SysCustomLinkDisplayName: string;
	// SysLinkTemplate: number;
	// MDOT_DBBARCODE
	SysBarFont: number;
	SysBarFontColor: string;
	SysBarBGStyle: boolean;
	SysBarSymbology: number;
	SysBarMessage: number;
	SysBarComment: number;
	SysBarUPCSupplemental: number;
	SysBarNarrowBarWidth: number;
	SysBarWidthReduction: number;
	SysBarCodeHeight: number;
	SysBarNarrowToWideRatio: number;
	SysBarCommentOnTop: boolean;
	SysBarIncludeText: boolean;
	SysBarTextOnTop: boolean;
	SysBarIncludeQZ: boolean;
	SysBarBBars: boolean;
	SysBarC39CHK: boolean;
	SysShowLuhnCheckDigit: boolean;
	// MDOT_TEXT, MDOT_DBTEXT
	SysVDO: boolean;
	SysVDOData: number;
	SysDBTextAutoSize: boolean;
	// OT_IMAGE, OT_BITMAP, MDOT_DBIMAGE
	CircleMask: number;
	Transparency: number;
	// MDOT_CONTAINER, MDOT_VIO
	SysAddress: string;
	SysVOKey: boolean;
	SysClickAction: string;
	// MDOT_CONTROLZONE
	SysCZAddress: string;
	SysCZControlZoneID: number;
	SysCZControllerID: number;
	SysCZAlarmColor: string;
	SysCZSecureColor: string;
	SysCZMaskedColor: string;
	SysCZUnknownColor: string;
	// MDOT_PMZ
	SysPMZAlarmColor: string;
	SysPMZSecureColor: string;
	SysPMZMaskedColor: string;
	SysPMZUnknownColor: string;
	// MDOT_IDS_AREA
	SysIDSAREAAreaID: number;
	SysIDSAREAPluginID: number;
	SysIDSAreaAlarmColor: string;
	SysIDSAreaSecureColor: string;
	// Common
	SysUDF: number;
	/* SysLayer: string;
	ObjTop: number;
	ObjBottom: number;
	ObjLeft: number;
	ObjRight: number;
	ObjRotation: number;
	ObjVisible: boolean;
	LineWidth: number;
	LineColor: string;
	LineStyle: number;
	FillColor: string;
	FillStyle: number;
	BackColor: string;
	BackStyle: number; */
};

export type Properties2DTO = {
	pName: string;
	pDebugText: boolean;
	pAlarmFlash: boolean;
	pObjectType: number;
	// MDOT_LINK
	pLinkData: number;
	// MDOT_COMMANDSET
	pCommandSetData: string;
	pCSLinkTemplate: number;
	// MDOT_CUSTOMLINK
	pCustom: number;
	pLinkTemplate: number;
	// MDOT_DBBARCODE
	pBarcodeFont: number;
	pBarcodeFontColor: string;
	pBarcodeBackgroundStyle: boolean;
	pBarcodeSymbology: number;
	pBarcodeData: number;
	pBarcodeComment: number;
	pBarcodeSupplemental: number;
	pBarcodeNarrowBarWidth: number;
	pBarcodeHeight: number;
	pBarcodeNarrowToWideRatio: number;
	pBarcodeCommentOnTop: boolean;
	pBarcodeIncludeText: boolean;
	pBarcodeTextOnTop: boolean;
	pBarcodeShowQuietZones: boolean;
	pBarcodeBBars: boolean;
	pBarcodec39CHK: boolean;
	pBarcodecLuhnCHK: boolean;
	// MDOT_TEXT, MDOT_DBTEXT
	pData: number;
	pAutoSizeDBText: boolean;
	// MDOT_DBMAGSTRIPE
	pMagstripeData: number;
	// OT_IMAGE, OT_BITMAP, MDOT_DBIMAGE
	pShape: number;
	pTransparency: number;
	// MDOT_CONTAINER, MDOT_VIO
	pAddress: string;
	pPrimaryVO: boolean;
	// MDOT_CONTROLZONE
	pCZAddress: string;
	pCZID: number;
	pCZAlarmColor: string;
	pCZSecureColor: string;
	pCZMaskedColor: string;
	pCZUnknownColor: string;
	// MDOT_PMZ
	pAddPMZPoints: number;
	pPZAlarmColor: string;
	pPZSecureColor: string;
	pPZMaskedColor: string;
	pPZUnknownColor: string;
	// Common
	pFont: number;
	pTextColor: string;
	pText: string;
	pTextStyle: number;
	pClick: string;
	pTop: number;
	pBottom: number;
	pLeft: number;
	pRight: number;
	pRotation: number;
	pVisible: boolean;
	pLayer: string;
	pLineWidth: number;
	pLineColor: string;
	pLineStyle: number;
	pFillColor: string;
	pFillStyle: number;
	pBackColor: string;
	pBackStyle: number;
};

export enum ImageType {
	None = 0,
	Photo = -1,
	Signature = -2,
}

export type SaveBadgeStudioDTO = {
	BadgeInfo: BadgeStudio;
	SpaceObjects: BadgeStudioSpaceObjects;
};

export type SetGraphicTextPayload = {
	itemId: number;
	newItem: GraphicText;
};
export type SetBadgeStudioMagstripePayload = {
	itemId: number;
	newItem: BadgeStudioMagstripe;
};
export type SetVisualShapeObjectPayload = {
	itemId: number;
	newItem: ShapeObject;
};
export type SetBadgeStudioImagePayload = {
	itemId: number;
	newItem: BadgeStudioImage;
};
export type SetBadgeStudioBarCodePayload = {
	itemId: number;
	newItem: BadgeStudioBarCode;
};
export type SetBadgeStudioInfoPayload = {
	itemId: number;
	newItem: BadgeStudio;
};
